import { _ } from "../../core/polyglot/polyglot.js";
import {
	chevronDownIcon,
	chevronUpIcon,
	expandArrowsAltIcon,
	flagIcon,
	pencilSolidIcon,
	plusIcon,
	saveIcon,
	timesIcon,
} from "../../core/elements/icons/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import { notBlank, validateAll } from "../../core/widgets/forms/validators.js";
import { warnDialog } from "../../core/widgets/dialogs/dialogs.js";
import accountingDocument from "../shared/accountingDocument.js";
import ActionButtonWidget from "../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import Agency from "../../models/Agency.js";
import badge from "../../core/elements/badge/badge.js";
import ChartOfAccounts from "../../models/chartOfAccounts/ChartOfAccounts.js";
import ChartOfAccountsGroupSelectionDialog from "./ChartOfAccountsGroupSelectionDialog.js";
import chartOfAccountsValidationResultFactory from "../../models/chartOfAccounts/validators/chartOfAccountsValidationResultFactory.js";
import chartOfAccountsViewerWidget from "./chartOfAccountsViewerWidget.js";
import exceptionsListWidget from "../../core/widgets/exceptionsListWidget.js";
import inputFieldDialog from "../../core/inputFieldDialog.js";
import inputFieldWidget from "../../core/widgets/forms/fields/inputFieldWidget.js";
import InternationalizationService from "../../core/services/InternationalizationService.js";
import LanguageChooserWidget from "../../core/widgets/settings/LanguageChooserWidget.js";
import regionWidget from "../../core/widgets/regionWidget.js";
import responsive from "../../core/responsive.js";
import session from "../../core/session.js";
import spacer from "../../core/elements/spacer/spacer.js";

/**
 * Document to edit a chart of accounts tree. A chart of accounts tree is
 * created by derivating from an already existing chart of accounts.
 *
 * Supported features:
 *     - Add/Edit a group
 *         - Change the name
 *         - Link/Unlink with a known group
 *     - Move a group
 *     - Delete a group
 *     - Reorder a group
 *     - Add an interval
 *
 * Unsupported features:
 *     - Change CoA language
 *     - Create a new chart of accounts from scratch
 */
function chartOfAccountsEditorDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = accountingDocument(spec, my);

	that.title = function (html) {
		html.render(_("Chart of Accounts Editor"));
	};

	that.subtitle = regionWidget({
		content: function (html) {
			if (my.chartOfAccounts) {
				my.renderChartOfAccountsNameOn(html);
			} else {
				html.render(_("Loading chart of accounts..."));
			}
		},
	});

	my.renderChartOfAccountsNameOn = function (html) {
		spacer(
			{ renderOn: html },
			_("Editing {name}", { name: my.chartOfAccounts.getName() }),
			(html) => {
				if (!my.chartOfAccounts.belongsTo(my.agency)) return;
				badge({ renderOn: html, label: _("shared") });
			},
		);
	};

	that.help = {
		summary: _(
			"Not all companies use the same account ranges and naming in their financial statements. This chart of accounts editor allows you to customize ranges and rename groups so that the reports and KPIs show the correct numbers and has the right look-and-feel. The concept of “mandatory groups” are used to edit the layout of the income and balance statements and group how account ranges will calculate KPIs.",
		),
		sections: [
			{
				title: _("Account tree"),
				content: function (html) {
					html.p(
						_(
							"A tree structure with the four main groups: Income, Costs, Assets, Equity & Liabilities. Under these there are subgroups and account ranges.",
						),
					);

					html.p(
						_(
							"The menu on the right hand side offers actions to customize the account tree. User may create account groups, rename existing account groups, remove account groups and move account groups up and down in the chart of accounts tree.",
						),
					);

					html.p(
						_(
							"The subgroups are visible in the income and balance statements by selecting different levels (level 1-4) in the reports.",
						),
					);
				},
			},
			{
				title: _("Mandatory groups"),
				content: function (html) {
					html.p(
						_(
							"The padlock icon means that it is a mandatory group that cannot be removed. However, removing the account ranges under that group means it will not be displayed in the reports.",
						),
					);

					html.p(
						_(
							"The mandatory groups are used to calculate KPIs, e.g. Netsales, and to define sections in the income and balance statements.",
						),
					);
				},
			},
			{
				title: _("Filter accounts/groups"),
				content: function (html) {
					html.p(
						_(
							"By searching for numbers or names a user can quickly find the group or account in the account tree.",
						),
					);
				},
			},
			{
				title: _("Save/Save As - important"),
				content: function (html) {
					html.p(
						_(
							"When you are done with the changes you must use the Save/Save As function. If you don’t do this all your changes will be lost. The standard charts of accounts cannot be changed or deleted. This means that the user has to click “Save As” and give the edited chart of accounts a new name.",
						),
					);

					html.p(
						_(
							"Once you have created a new chart of accounts, you can edit or delete it.",
						),
					);
				},
			},
			{
				title: _("Apply customized chart of accounts to reports"),
				content: function (html) {
					html.p(
						_(
							"When creating a new chart of accounts, it needs to be applied to a fiscal year before any changes occur in the reports and KPIs. To apply a chart of accounts, go to Company Settings and choose “edit” besides the fiscal year. Then select the chart of accounts you wish to use in the drop down menu and click “Update settings”. If you want to use a new chart of accounts on all fiscal years, you have to do the same procedure on each fiscal year.",
						),
					);
				},
			},
		],
	};

	that.hasActionsMenu = function () {
		return my.canEditChartOfAccounts();
	};

	that.documentId = "chart-of-accounts";

	that.hasPeriodPicker = function () {
		return false;
	};

	const antiForgeryToken = session.getAntiForgeryToken();
	my.agency = Agency.materialize(session.agency);

	const canShareChartOfAccounts = session
		.getCurrentUser()
		.canShareChartOfAccounts();

	my.agency = Agency.materialize(session.agency);
	my.company = session.getCompany();

	my.chartOfAccountsRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/ChartOfAccounts/?id",
		model: ChartOfAccounts,
		defaultFilters: {
			companyId: my.company.getLegacyId(),
		},
		antiForgeryToken,
	});

	my.validateRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/ChartOfAccounts/Validate",
		model: chartOfAccountsValidationResultFactory,
		context: {
			chartOfAccounts: () => my.chartOfAccounts,
		},
		defaultFilters: {
			companyId: my.company.getLegacyId(),
		},
		antiForgeryToken,
	});

	my.chartOfAccounts = null;
	my.usedChartsOfAccountsNames = null;

	my.onActionPerformed = my.events.createEvent();
	my.onActionPerformed.register(validate);

	my.coaViewer = chartOfAccountsViewerWidget();
	my.coaViewer.onSelectionChange.register(() => my.updateActions());

	my.errorsList = exceptionsListWidget();

	that.editChartOfAccounts = async function (id) {
		that.showUpdating();

		const chartOfAccounts = await my.chartOfAccountsRepository.get({
			filter: {
				id,
			},
		});

		my.setChartOfAccounts(chartOfAccounts);
		that.showContent();
		my.updateActions();
		if (!responsive.isMobileOrIPadPortaitBreakpoint()) {
			my.showActions();
		}
	};

	that.actions = function () {
		if (!my.chartOfAccounts) {
			return [];
		}

		const userDefined = my.chartOfAccounts.isUserDefined();
		const selected = my.selectedGroup();
		const selectedParent =
			my.selectedGroupParent() || my.chartOfAccounts.getMainTree();

		return [
			{
				label: _("Language"),
				children: [
					{
						content: new ActionButtonWidget({
							label: _("Set language"),
							click: () => my.setLanguage(),
							icon: flagIcon,
						}),
					},
				],
			},
			{
				label: _("Add"),
				condition: () => selected,
				children: [
					{
						content: new ActionButtonWidget({
							label: _("Add group"),
							click: () => my.addGroup(selected),
							icon: plusIcon,
							dataTestName: "add-group",
						}),
						condition: () =>
							selected.isAccountGroup() && selected.canAddGroup(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Add interval"),
							click: () => my.addInterval(selected),
							icon: plusIcon,
						}),
						condition: () =>
							selected.isAccountGroup() &&
							selected.canAddInterval(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Add accounts"),
							click: () => my.addAccountList(selected),
							icon: plusIcon,
						}),
						condition: () =>
							selected.isAccountGroup() &&
							selected.canAddAccountList(),
					},
				],
			},
			{
				label: _("Edit"),
				condition: () => selected,
				children: [
					{
						content: new ActionButtonWidget({
							label: _("Edit interval"),
							click: () => my.editInterval(selected),
							icon: pencilSolidIcon,
							dataTestName: "edit-interval",
						}),
						condition: () => selected.isInterval(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Remove interval"),
							click: () =>
								my.removeInterval(selected, selectedParent),
							icon: timesIcon,
						}),
						condition: () => selected.isInterval(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Edit accounts"),
							click: () => my.editAccountList(selected),
							icon: pencilSolidIcon,
						}),
						condition: () => selected.isAccountList(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Remove selected accounts"),
							click: () =>
								my.removeAccountList(selected, selectedParent),
							icon: timesIcon,
						}),
						condition: () => selected.isAccountList(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Edit group"),
							click: () => my.editGroup(selected),
							icon: pencilSolidIcon,
						}),
						condition: () =>
							selected.isAccountGroup() && selected.canBeEdited(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Remove group"),
							click: () =>
								my.removeAccountGroup(selected, selectedParent),
							icon: timesIcon,
						}),
						condition: () =>
							selected.isAccountGroup() &&
							selectedParent.canRemove(selected),
					},
				],
			},
			{
				label: _("Move"),
				condition: () => selected,
				children: [
					{
						content: new ActionButtonWidget({
							label: _("Move group"),
							dataTestName: "move",
							click: () => my.moveGroup(selected, selectedParent),
							icon: expandArrowsAltIcon,
						}),
						condition: () => selected.isAccountGroup(),
					},
					{
						content: new ActionButtonWidget({
							label: _("Move up"),
							click: () => my.moveUp(selected, selectedParent),
							icon: chevronUpIcon,
							isDisabled: () =>
								!selectedParent.canMoveUp(selected),
						}),
					},
					{
						content: new ActionButtonWidget({
							label: _("Move down"),
							click: () => my.moveDown(selected, selectedParent),
							icon: chevronDownIcon,
							isDisabled: () =>
								!selectedParent.canMoveDown(selected),
						}),
					},
				],
			},
			{
				label: _("Validate"),
				children: [
					{
						content: function (html) {
							let checkbox;

							html.div(
								{ klass: "checkbox" },
								html.label((html) => {
									checkbox = html.input({ type: "checkbox" });
								}, _("Validate interval gaps")),
							);

							if (my.chartOfAccounts.validatesGaps()) {
								checkbox.attr({ checked: "checked" });
							}

							checkbox.on("change", (event) => {
								my.setGapsValidation(event.target.checked);
							});
						},
					},
				],
			},
			{
				label: _("Save"),
				children: [
					{
						content: new ActionButtonWidget({
							label: _("Save"),
							click: async () => {
								const shouldSave = await my.shouldSave();
								if (!shouldSave) return;
								save();
							},
							icon: saveIcon,
							isPrimary: true,
							dataTestName: "save",
						}),
						condition: () => userDefined,
					},
					{
						content: new ActionButtonWidget({
							label: _("Save as..."),
							click: () => saveAs(),
							icon: saveIcon,
							isPrimary: () => !userDefined,
							isDisabled: () => !my.canSave(),
							dataTestName: "save-as",
						}),
					},
				],
			},
		];
	};

	my.renderContentOn = function (html) {
		if (my.canEditChartOfAccounts()) {
			html.div(
				{ klass: "col-md-10 col-md-offset-1" },
				my.errorsList,
				my.coaViewer,
			);
		} else {
			my.renderNotEditableOn(html);
		}
	};

	my.renderNotEditableOn = function (html) {
		html.div(
			{ klass: "information-box" },
			html.h2(
				_("{name} cannot be edited", {
					name: my.chartOfAccounts.getName(),
				}),
			),
			html.p(
				_(
					"The editor does not support the chart of accounts {name} yet.",
					{
						name: my.chartOfAccounts.getName(),
					},
				),
			),
			html.div(
				{ klass: "actions clearfix" },
				html
					.a({ klass: "pull-right btn btn-primary" }, _("Go back"))
					.click(() => {
						my.redirectTo("company-settings");
					}),
			),
		);
	};

	my.renderEmptyContentOn = function (html) {
		html.div(
			{ klass: "empty error" },
			_("No chart of accounts found for the provided id."),
		);
	};

	my.setChartOfAccounts = function (chartOfAccounts) {
		my.chartOfAccounts = chartOfAccounts;
		my.coaViewer.setChartOfAccounts(my.chartOfAccounts);

		my.updateActions();
		that.subtitle.update();
		that.update();
	};

	my.setGapsValidation = function (value) {
		my.chartOfAccounts.setGapsValidation(value);
		my.onActionPerformed.trigger();
	};

	my.canEditChartOfAccounts = function () {
		return my.chartOfAccounts?.isEditable();
	};

	my.selectedGroup = function () {
		return my.coaViewer.selectedGroup();
	};

	my.selectedGroupParent = function () {
		return my.coaViewer.selectedGroupParent();
	};

	my.setLanguage = function () {
		let currentLanguage = my.chartOfAccounts.getLanguage();

		const languageChooser = new LanguageChooserWidget({
			currentLanguage,
			service: InternationalizationService.instance(),
		});

		languageChooser.onUpdateLanguage((language) => {
			currentLanguage = language;
			languageChooser.update();
		});

		my.confirm({
			title: _("Set language"),
			body: languageChooser,
			confirmLabel: _("Save"),
			callback: () => my.chartOfAccounts.setLanguage(currentLanguage),
		});
	};

	my.addInterval = function (parent) {
		const interval = my.chartOfAccounts.createInterval({
			name: `${parent.getId()}-interval`,
			parent,
		});

		my.makeIntervalAccountGroupDialog(interval, () => {
			my.coaViewer.addChild(parent, interval);
			my.onActionPerformed.trigger();
		});
	};

	my.editInterval = function (interval) {
		my.makeIntervalAccountGroupDialog(interval, () => {
			my.coaViewer.updateGroup(interval);
			my.onActionPerformed.trigger();
		});
	};

	my.makeIntervalAccountGroupDialog = function (group, callback) {
		my.configure({
			title: (html) => {
				if (group.getIntervalsString()) {
					html.render(_("Edit interval"));
				} else {
					html.render(_("Add interval"));
				}
			},
			okLabel: _("Save"),
			formSpec: {
				saveAction: callback,
			},
			formBuilder: (form) => {
				form.input({
					name: "intervals",
					label: _("Intervals"),
					accessor: () => group.getIntervalsString(),
					mutator: (intervals) => group.setIntervals(intervals),
					placeholder: "3010-3100",
					help: _(
						'Enter one or many intervals. Example: "3000-3100,3200-3300"',
					),
					validator: notBlank(),
				});
			},
		});
	};

	my.removeInterval = function (interval, parent) {
		my.confirm({
			title: _("Delete interval"),
			body: _(
				'You are about to delete the interval {name} from "{parent}". Are you sure?',
				{
					name: interval.displayLabel(),
					parent: parent.displayLabel(),
				},
			),
			callback: () => {
				parent.removeChild(interval);
				my.coaViewer.removeGroup(interval, parent);
				my.onActionPerformed.trigger();
			},
		});
	};

	my.addAccountList = function (parent) {
		const accountsInput = inputFieldWidget({
			placeHolder: _("A, B, C"),
			takeFocus: true,
		});

		my.confirm({
			title: _("Add Accounts"),
			body: accountsInput,
			callback: () => {
				const accountList = my.chartOfAccounts.createAccountList({
					name: `${parent.getId()}-account_list`,
					commaSeparatedAccounts: accountsInput.getValue(),
					parent,
				});
				my.coaViewer.addChild(parent, accountList);
				my.onActionPerformed.trigger();
			},
		});
	};

	my.editAccountList = function (accountList) {
		const accountsInput = inputFieldWidget({
			placeHolder: _("A, B, C"),
			takeFocus: true,
			value: accountList.getAsCommaSeparatedString(),
		});

		my.confirm({
			title: _("Edit accounts"),
			body: accountsInput,
			callback: () => {
				accountList.setFromCommaSeparatedString(
					accountsInput.getValue(),
				);

				my.coaViewer.updateGroup(accountList);
				my.onActionPerformed.trigger();
			},
		});
	};

	my.removeAccountList = function (accountList, parent) {
		my.confirm({
			title: _("Delete accounts"),
			body: _(
				'You are about to delete "{name}" from "{parent}". Are you sure?',
				{
					name: accountList.displayLabel(),
					parent: parent.displayLabel(),
				},
			),
			callback: () => {
				parent.removeChild(accountList);
				my.coaViewer.removeGroup(accountList, parent);
				my.onActionPerformed.trigger();
			},
		});
	};

	my.addGroup = function (parent) {
		const dialog = inputFieldDialog({
			header: _("Add group"),
			inputLabel: _("Name"),
			placeholder: _("Name"),
			inputValidator: function (name) {
				if (name) {
					return;
				}
				throw new Error(_("Name is not provided"));
			},
		});

		dialog.onConfirm.register((name) => {
			const group = my.chartOfAccounts.createGroup({
				name,
				parent,
			});
			my.coaViewer.addChild(parent, group);
			my.onActionPerformed.trigger();
		});

		dialog.show();
	};

	my.editGroup = function (group) {
		const dialog = inputFieldDialog({
			header: _("Edit {name}", { name: group.getName() }),
			inputLabel: _("Name"),
			initialValue: group.getName(),
			placeholder: _("Name"),
			inputValidator: function (name) {
				if (name) {
					return;
				}
				throw new Error(_("Name is not provided"));
			},
		});

		dialog.onConfirm.register((name) => {
			group.setName(name);
			my.coaViewer.updateGroup(group);
			my.onActionPerformed.trigger();
			my.updateActions();
		});

		dialog.show();
	};

	my.moveGroup = function (group, parent) {
		new ChartOfAccountsGroupSelectionDialog({
			chartOfAccounts: my.chartOfAccounts,
			title: _("Move {group}", {
				group: group.getName(),
			}),
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			canBeSelected: function (parent) {
				return parent.canAdd(group);
			},
			group,
			callback: (target) => {
				target.addChild(group);
				parent.removeChild(group);
				my.coaViewer.removeGroup(group, parent);
				my.coaViewer.addChild(target, group);

				my.onActionPerformed.trigger();
			},
		}).show();
	};

	my.moveUp = function (group, parent) {
		group.moveUp(parent);
		my.coaViewer.forceUpdateParentGroupFor(group, parent);
	};

	my.moveDown = function (group, parent) {
		group.moveDown(parent);
		my.coaViewer.forceUpdateParentGroupFor(group, parent);
	};

	my.removeAccountGroup = function (group, parent) {
		my.confirm({
			title: _("Delete {name}", {
				name: group.getName(),
			}),
			body: _(
				'You are about to delete the group "{name}" from "{parent}". Are you sure?',
				{
					name: group.displayLabel(),
					parent: parent.displayLabel(),
				},
			),
			callback: () => {
				parent.removeChild(group);
				my.coaViewer.removeGroup(group, parent);
				my.onActionPerformed.trigger();
			},
		});
	};

	my.setErrors = function (errors) {
		const result = {};

		result.errors = {
			title: _("Errors"),
			exceptions: errors,
		};

		my.errorsList.setExceptions(result);
	};

	my.canSave = function () {
		return !!my.usedChartsOfAccountsNames;
	};

	my.shouldSave = function () {
		if (!my.chartOfAccounts.belongsTo(my.agency))
			return Promise.resolve(true);

		const name = my.chartOfAccounts.getName();

		return new Promise((resolve) => {
			warnDialog({
				name,
				title: _("Changing chart of accounts {name}", { name }),
				body: _(
					"Be aware that by saving, all changes you've made will affect all companies in {agencyName} that are using this chart of accounts.",
					{ agencyName: my.agency.getName() },
				),
				callback: () => resolve(true),
				onCancel: () => resolve(false),
				cssClass: "",
			}).show();
		});
	};

	async function save() {
		try {
			await my.chartOfAccountsRepository.update({
				notify: false,
				model: my.chartOfAccounts,
				filter: {
					id: my.chartOfAccounts.getId(),
				},
			});

			my.errorsList.removeExceptions();

			my.success(
				_("{name} saved", {
					name: my.chartOfAccounts.getName(),
				}),
			);
		} catch (error) {
			handleErrorData(error);
		}
	}

	function saveAs() {
		my.configure({
			title: _("Save chart of accounts as..."),
			subtitle: _(
				"You are about to save the chart of accounts. Please choose a name for it.",
			),
			okLabel: _("Confirm"),
			formSpec: { saveAction: saveChartOfAccounts },
			formBuilder: (form) => {
				form.input({
					label: _("Name"),
					attribute: "name",
					validator: validateAll(notBlank(), nameValidator),
				});

				if (canShareChartOfAccounts) {
					form.checkbox({
						text: _("Shared with other companies"),
						attribute: "isShared",
						dataTestName: "shared",
						reversed: true,
					});
				}
			},
		});
	}

	async function saveChartOfAccounts({ name, isShared }) {
		const oldName = my.chartOfAccounts.getName();
		my.chartOfAccounts.setName(name);

		const oldOwnerType = my.chartOfAccounts.getOwnerType();
		const oldOwnerId = my.chartOfAccounts.getOwnerId();

		const owner = isShared ? my.agency : my.company;
		my.chartOfAccounts.setOwnership(owner);

		try {
			const model = await my.chartOfAccountsRepository.save({
				notify: false,
				model: my.chartOfAccounts,
			});
			my.errorsList.removeExceptions();

			my.redirectTo("chart-of-accounts-editor/?id", {
				id: model.getId(),
			});
		} catch (error) {
			my.error(_("Failed to save the chart of accounts."));
			handleErrorData(error);
			my.chartOfAccounts.setName(oldName);
			my.chartOfAccounts.setOwnerType(oldOwnerType);
			my.chartOfAccounts.setOwnerId(oldOwnerId);
		}
	}

	function nameValidator(name) {
		if (my.usedChartsOfAccountsNames.indexOf(name) === -1) return;
		throw _('Name "{name}" is already used', { name });
	}

	async function validate() {
		try {
			await my.validateRepository.save({
				notify: false,
				model: my.chartOfAccounts,
			});

			my.errorsList.removeExceptions();
		} catch (error) {
			handleErrorData(error);
		}
	}

	function handleErrorData(data) {
		if (!data.responseJSON) {
			my.error(data.statusText);
			return;
		}

		if (!data.responseJSON.errors) {
			my.error(data.responseJSON.message);
			return;
		}

		const errors = data.responseJSON.errors;

		my.setErrors(
			chartOfAccountsValidationResultFactory.materializeAll(errors, {
				chartOfAccounts: my.chartOfAccounts,
			}),
		);

		that.update();
	}

	that.showEmpty();

	(async () => {
		const chartsInfo = await my.chartOfAccountsRepository.get();

		my.usedChartsOfAccountsNames = chartsInfo.map((info) => info.getName());

		my.updateActions();
	})();

	return that;
}

export default chartOfAccountsEditorDocument;
